import { BusyIndicator } from "client/Components";
import { AlertManager, useAlerts, useValueEqualMemo } from "client/Hooks";
import {
	isNotNil
} from "client/Misc/utils";
import { hasImplicitDependencyOn } from "client/Misc/reactUtils";
import {
	ReactElement,
	RefObject,
	useCallback,
	useEffect,
	useRef,
	useState
} from "react";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import { Guid } from "client_legacy/ndaw/guid";
import { DimensionToRender } from "../../RenderState/common";
import { LiveTileContent } from "../LiveTileContent";
import { TileRefresh } from "../TileRefresh/TileRefresh";
import { HubTileCategoryIds, ListItemProps } from "./common";
import { HubItemLiveInformationProvider, useHubItemLiveInformation } from "./hooks";
import { EmptyResult, ErrorResult } from "./NoResults";
import { TimeZone } from "client/DateAndTime/timezones";
import { HubItemTileResourceKind } from "client/Api/Workspace/workspace";

export type ListLayoutProps<TItem extends { id: number; }, TInterests> = {
	tabId: Guid;
	categoryIds: HubTileCategoryIds;
	dimension: DimensionToRender;
	provider: HubItemLiveInformationProvider<TItem, TInterests>;
	interestsFactory: (isWideTile: boolean) => Required<TInterests>;
	listItemComponent: {
		(props: ListItemProps<TItem>): ReactElement;
	};
	onOpen: (id: number) => void;
	timezone: TimeZone;
};

export const ListLayout = <TItem extends { id: number; }, TInterests>(
	props: ListLayoutProps<TItem, TInterests>
): ReactElement => {
	const {
		tabId,
		categoryIds,
		dimension,
		provider,
		interestsFactory,
		listItemComponent: ListItemComponent,
		onOpen,
		timezone
	} = props;

	const [additionalPages, setAdditionalPages] = useState<readonly AdditionalPage<TItem>[]>([]);
	const additionalPagesAbortControllerRef: RefObject<AbortController | null> = useRef<AbortController>(null);

	const alertManager: AlertManager = useAlerts();

	const isWideTile: boolean = dimension.kind === "normal"
		&& dimension.dimension
			.columnSpan > 4;

	const isUltraWideTile: boolean = dimension.kind === "normal"
		&& dimension.dimension
			.columnSpan > 6;

	const unstableInterests: Required<TInterests> = interestsFactory(isWideTile);
	const interests = useValueEqualMemo(
		(): Required<TInterests> => unstableInterests,
		[unstableInterests]
	);

	useEffect(
		(): void => {
			hasImplicitDependencyOn(categoryIds);
			hasImplicitDependencyOn(provider);
			additionalPagesAbortControllerRef.current
				?.abort();
			setAdditionalPages([]);
		},
		[categoryIds, provider]
	);

	const [loadingState, refreshHandler] = useHubItemLiveInformation(
		provider,
		tabId,
		{
			kind: HubItemTileResourceKind.CategoryIds,
			categoryIds
		},
		additionalPages.length !== 0
			? undefined
			: interests
	);

	useEffect(
		(): (() => void) => {
			return () => additionalPagesAbortControllerRef.current
				?.abort();
		},
		[]
	);

	const scrollHandler = useCallback(
		(scrollTop: number): void => {
			if (scrollTop <= 0) {
				additionalPagesAbortControllerRef.current
					?.abort();
				setAdditionalPages([]);
			}
		},
		[]
	);

	const loadMoreHandler = useCallback(
		async (): Promise<void> => {
			const lastAdditionalPage: AdditionalPage<TItem> | undefined = additionalPages[additionalPages.length - 1];
			let lastPage: LazyLoadPageResult<TItem> | undefined = loadingState.page;
			if (lastAdditionalPage) {
				if (lastAdditionalPage.isLoading) {
					throw new Error("Cannot load more while still loading the additional page");
				}

				lastPage = lastAdditionalPage.page;
			}

			if (!lastPage?.hasMore) {
				throw new Error("Cannot load more because the last page has no more items");
			}

			const { items } = lastPage;
			const lastLoadedItem: TItem = items[items.length - 1];

			setAdditionalPages(
				x => [
					...x,
					{
						isLoading: true,
						page: undefined
					}
				]
			);

			try {
				additionalPagesAbortControllerRef.current
					?.abort();

				const cancellation = new AbortController();
				additionalPagesAbortControllerRef.current = cancellation;

				const page: LazyLoadPageResult<TItem> = await provider
					.loadMoreItems(
						categoryIds,
						interests,
						lastLoadedItem,
						cancellation.signal
					);

				setAdditionalPages(
					x => {
						const copy: AdditionalPage<TItem>[] = [...x];

						const lastPage: AdditionalPage<TItem> | undefined = copy[copy.length - 1];

						if (lastPage) {
							if (!lastPage.isLoading) {
								console.warn("The last page is not loading anymore");
							} else {
								copy.pop();
								copy.push({
									isLoading: false,
									page
								});
							}
						}

						return copy;
					}
				);
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}
				console.error(e);
				alertManager.showError(localize("_GeneralErrorMessage"));
			}
		},
		[
			additionalPages,
			alertManager,
			categoryIds,
			interests,
			loadingState.page,
			provider
		]
	);

	const {
		isLoading: isLiveInformationLoading,
		hasTooManyConsecutiveErrors,
		hasGlobalFailure,
		page,
		lastPinnedItem
	} = loadingState;

	const isLoading: boolean = additionalPages[additionalPages.length - 1]
		?.isLoading ?? isLiveInformationLoading;

	const renderContent = (): ReactElement | null => {

		if (hasTooManyConsecutiveErrors) {
			return <TileRefresh onRefresh={refreshHandler} />;
		}

		if (hasGlobalFailure) {
			return <ErrorResult itemKind={provider.itemKind} onRefresh={refreshHandler} />;
		}

		if (!page) {
			return null;
		}

		if (page.items.length === 0) {
			return <EmptyResult itemKind={provider.itemKind} categoryIds={categoryIds} />;
		}

		let allItems: TItem[] = [
			page,
			...additionalPages.map(x => x.page)
				.filter(isNotNil)
		]
			.flatMap(x => x.items);

		if (lastPinnedItem) {
			allItems = allItems
				.filter(
					x => x.id !== lastPinnedItem.id
				);

			allItems.unshift(lastPinnedItem);
		}

		const lastAdditionalPage: AdditionalPage<TItem> | undefined = additionalPages[additionalPages.length - 1];
		let hasMore: boolean = page.hasMore;
		if (lastAdditionalPage) {
			hasMore = lastAdditionalPage.page
				?.hasMore ?? false;
		}

		return (
			<LiveTileContent.Scrollable onScroll={scrollHandler}>
				<LiveTileContent.List trackingId="hub-items-list">
					{allItems.map((x) => (
						<ListItemComponent
							key={x.id}
							item={x}
							isWideTile={isWideTile}
							isUltraWideTile={isUltraWideTile}
							onOpen={onOpen}
							timezone={timezone}
						/>
					))}
				</LiveTileContent.List>
				{hasMore && (
					<LiveTileContent.LoadMoreButton onClick={loadMoreHandler}/>
				)}
			</LiveTileContent.Scrollable>
		);
	};

	return (
		<LiveTileContent.BusyIndicatorContainer>
			{isLoading && (
				<BusyIndicator />
			)}

			{renderContent()}
		</LiveTileContent.BusyIndicatorContainer>
	);
};
ListLayout.displayName = "ListLayout";

type AdditionalPage<TItem extends { id: number; }> = {
	isLoading: boolean;
	page: LazyLoadPageResult<TItem> | undefined;
};
